import React from 'react';
import { CardElevation, CardProps } from '../../../types/DesignSystem/Card';
import Typography from '../Typography/Typography';
import { withClassName } from '../cn';

/**
 * Elevation → class.
 *
 * **`ds-sm` had no consumer at all before this.** The three-stop elevation scale was
 * added to `tailwind.config.js` as a set, and `ds-md` (Dropdown's menu) and `ds-lg`
 * (Dialog, SidePanel, DropdownButton, Toast) were both spent immediately — `ds-sm`
 * sat unused, so every card-shaped surface in the product was a flat border. This is
 * the tier it was defined for.
 */
const elevationClass: Record<CardElevation, string> = {
  none: '',
  sm: 'tw-shadow-ds-sm',
  md: 'tw-shadow-ds-md',
};

/*
 * § 360 — the stretched target. The title keeps its own box; its `::after` is stretched
 * over the card (`inset-0` against the card's `relative` root), so the hit area is the card
 * while the control — and its name — is the title. The focus ring is FOCUS_RING's recipe
 * under `after:`, drawn on that stretched box, so a keyboard user sees the target a pointer
 * has; the element's own outline is zeroed so the ring is not drawn twice. Literals, because
 * the class scanner cannot see a template.
 */
const CARD_TARGET =
  'tw-text-inherit tw-no-underline tw-text-left tw-bg-transparent tw-border-0 tw-p-0 tw-m-0 tw-cursor-pointer '
  + 'focus-visible:tw-outline focus-visible:tw-outline-0 '
  + "after:tw-content-[''] after:tw-absolute after:tw-inset-0 after:tw-rounded-srf "
  + 'focus-visible:after:tw-outline focus-visible:after:tw-outline-2 focus-visible:after:tw-outline-transparent focus-visible:after:tw-outline-offset-2 '
  + 'focus-visible:after:tw-ring-2 focus-visible:after:tw-ring-focus '
  + 'focus-visible:after:tw-ring-offset-2 focus-visible:after:tw-ring-offset-surface';

/**
 * A surface. Border, radius, elevation and a padding rhythm — nothing else.
 *
 * **Padding lives on the sections, not the root**, which looks like the wrong choice
 * until you add a footer: the footer is a full-bleed band with a top rule and a
 * `grey-100` fill (the same treatment `Dialog` uses — § 33), and a band cannot reach
 * the card's edges from inside a padded root. So each section owns its inset and the
 * body drops its top padding when a header is already above it, rather than stacking
 * two paddings into a gap twice the size of the others.
 *
 * Props rather than compound sub-components (`Card.Header`, `Card.Body`): `Dialog`
 * already established the props shape in this system for exactly this anatomy, and a
 * compound API here would mean two ways to build the same box.
 */
/*
 * PILOT of the semantic role layer (2026-08-25): this component names roles
 * (`ink`, `surface`, `edge`) instead of palette steps. Same rendered colours —
 * `semanticTokens.test.ts` pins each role to the exact step this file used to name.
 * The rest of the system migrates per-component; see palette.ts `ROLES`.
 */
const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card({
  title,
  description,
  footer,
  elevation = 'sm',
  interactive = false,
  action,
  titleAs = 'h3',
  className = '',
  'data-testid': dataTestId,
  children,
  ...rest
}, ref) {
  const hasHeader = Boolean(title || description);
  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);
  /* § 360 — a card with somewhere to go is ONE target; `{}` (neither half) is not. */
  const target = action && (action.href !== undefined || action.onClick !== undefined) ? action : undefined;
  const lifts = interactive || target !== undefined;
  const descriptionId = `${React.useId()}-description`;

  return (
    <div
      /* rest first — see the rule in Button.tsx. */
      {...rest}
      ref={ref}
      /* Elevated cards sit on `surface-raised`: same white in light (the shadow
         does the work there), one rung lighter in dark, where an ink-tinted
         shadow is invisible and lightening is what elevation looks like. */
      /* § 312 — the card is a CONTAINER. Its footer asks how wide the card is, not
         how wide the window is: a card in a 416px SidePanel was being handed the
         same desktop action row as one spanning a 1440px page.

         `tw-cq` is `container-type: inline-size`, which implies `contain: layout
         style`. Two consequences worth knowing before putting anything unusual in a
         card: it opens a new stacking context, and it becomes the containing block
         for any `position: fixed` descendant. The four anchored design-system
         surfaces are portalled by default (§ 304) so they are unaffected; a consumer
         hand-rolling a fixed overlay INSIDE a card would now position against the
         card. `tw-cq-none` on a wrapper is the way out. */
      className={withClassName(`tw-cq ${target ? 'tw-relative ' : ''}${elevation === 'none' ? 'tw-bg-surface' : 'tw-bg-surface-raised'} tw-border tw-border-edge tw-rounded-srf ${elevationClass[elevation]} ${
        /* Lift ONE tier on hover, and only from sm — ds-lg is overlay-only (§ 42),
           so an md card has nowhere law-abiding to go and stays put.
           § 297 — the lift is three cues, not one. A shadow step alone is invisible in
           dark (near-black stops on a near-black page), so the card RISES 1px and its
           edge steps to `edge-strong` — the same 3:1 tier a control's boundary wears —
           and all three tween together on the ladder's fast rung. `transition-all`
           because transform and border-color are not in `transition-shadow`, and a
           comma inside an arbitrary value does not compile under a variant (motion.ts).
           The translate is motion-safe: under reduced motion the shadow and edge still
           change, the card just does not move. A transform makes a stacking context for
           the hovered frame; an interactive card is a click target and holds no popover. */
        lifts && elevation === 'sm'
          ? 'motion-safe:tw-transition-all motion-safe:tw-duration-fast motion-safe:tw-ease-swift hover:tw-shadow-ds-md hover:tw-border-edge-strong motion-safe:hover:-tw-translate-y-px'
          : ''
      }`, className)}
      data-testid={dataTestId}
    >
      {hasHeader && (
        <div className="tw-px-5 tw-pt-5" data-testid={testId('header')}>
          {title && (
            <Typography
              variant="title-header"
              as={titleAs}
              className="tw-text-ink"
              data-testid={testId('title')}
            >
              {target?.href !== undefined ? (
                <a
                  href={target.href}
                  onClick={target.onClick}
                  aria-describedby={description ? descriptionId : undefined}
                  className={CARD_TARGET}
                  data-testid={testId('action')}
                >
                  {title}
                </a>
              ) : target ? (
                <button
                  type="button"
                  onClick={target.onClick}
                  aria-describedby={description ? descriptionId : undefined}
                  className={CARD_TARGET}
                  data-testid={testId('action')}
                >
                  {title}
                </button>
              ) : (
                title
              )}
            </Typography>
          )}
          {description && (
            <Typography
              id={target ? descriptionId : undefined}
              variant="caption-metadata"
              className="tw-text-ink-secondary tw-mt-1 tw-block"
              data-testid={testId('description')}
            >
              {description}
            </Typography>
          )}
        </div>
      )}

      {children !== undefined && children !== null && children !== false && (
        /* `pt-4` under a header, `pt-5` without one: the header already supplies the
           top inset, so a full `p-5` here would read as a gap twice every other. */
        <div
          /* text-ink: the themed default for body content that brings no ink of its
             own (§ 55) — children's own classes still win. */
          className={`${hasHeader ? 'tw-px-5 tw-pb-5 tw-pt-4' : 'tw-p-5'} tw-text-ink`}
          data-testid={testId('body')}
        >
          {children}
        </div>
      )}

      {footer && (
        /* Full-bleed band, matching Dialog's footer: the rule and the fill are ONE
           band, not two separate lines (§ 33). `rounded-b-srf` keeps it inside the
           card's own corners. */
        /* § 296 — the band drops ONE rung from the card's own ground. An elevated
           card is `surface-raised`, so its footer is `surface-recess` (in dark, the
           `surface` value — `sunken` there is the page, two rungs down); a flat card
           sits on `surface`, so `sunken` IS its one rung. Light: one hex either way. */
        <div
          /* § 312 — narrow-first, then the container restores the row.
             Below 276px (the narrowest real container in the product) two buttons
             side by side are ~110px each with the 20px insets and the gap, which is
             where a two-word label starts wrapping mid-button. Stacked and stretched,
             each gets the full width.

             Written narrow-first rather than as a max-width override because the
             footer is rendered BY Card and can never be detached from the `tw-cq`
             root above — so the container always exists and `cq-xs:` always resolves.
             A detachable subcomponent would need the opposite structure, or a missing
             container would silently pin it to the narrow branch. */
          /* § 360 — above the stretched target, so the footer's buttons stay their own. */
          className={`${target ? 'tw-relative tw-z-10 ' : ''}tw-px-5 tw-py-4 tw-border-t tw-border-edge ${elevation === 'none' ? 'tw-bg-surface-sunken' : 'tw-bg-surface-recess'} tw-rounded-b-srf tw-flex tw-flex-col tw-items-stretch tw-gap-2 cq-xs:tw-flex-row cq-xs:tw-items-center cq-xs:tw-justify-end`}
          data-testid={testId('footer')}
        >
          {footer}
        </div>
      )}
    </div>
  );
});

export default Card;
